import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Mail } from 'lucide-react';
import Footer from './Footer';
import SectionEyebrow from './common/SectionEyebrow';
import '../assets/styles/SectionBand.css';
import '../assets/styles/PrivacyPolicy.css';
import { useLanguage } from '../assets/js/LanguageContext';
import { openCookieSettings } from '../utils/cookieConsent';

const LAST_UPDATED = '15/09/2026';
const LEGAL_EMAIL = 'support@thepul.se';

const PrivacyPolicy = () => {
  const { t } = useLanguage();
  const [activeId, setActiveId] = useState(null);

  const groups = useMemo(() => [
    {
      id: 'privacy-section',
      title: t('legal_privacy_title'),
      intro: t('legal_privacy_intro'),
      sections: [
        {
          id: 'privacy-responsible',
          title: t('legal_privacy_section_1_title'),
          content: (
            <dl className="legal-data">
              <div><dt>{t('legal_company_label')}</dt><dd>The Pulse</dd></div>
              <div><dt>{t('legal_contact_email_label')}</dt><dd><a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a></dd></div>
              <div><dt>{t('legal_address_label')}</dt><dd>{t('legal_address_value')}</dd></div>
            </dl>
          ),
        },
        {
          id: 'privacy-data',
          title: t('legal_privacy_section_2_title'),
          content: (
            <ul>
              <li><strong>{t('legal_privacy_data_identification_label')}</strong> {t('legal_privacy_data_identification_text')}</li>
              <li><strong>{t('legal_privacy_data_anonymous_label')}</strong> {t('legal_privacy_data_anonymous_text')}</li>
              <li><strong>{t('legal_privacy_data_technical_label')}</strong> {t('legal_privacy_data_technical_text')}</li>
            </ul>
          ),
        },
        {
          id: 'privacy-purposes',
          title: t('legal_privacy_section_3_title'),
          content: (
            <ul>
              <li>{t('legal_privacy_purpose_1')}</li>
              <li>{t('legal_privacy_purpose_2')}</li>
              <li>{t('legal_privacy_purpose_3')}</li>
              <li>{t('legal_privacy_purpose_4')}</li>
            </ul>
          ),
        },
        {
          id: 'privacy-legal-basis',
          title: t('legal_privacy_section_4_title'),
          content: (
            <ul>
              <li>{t('legal_privacy_legal_basis_1')}</li>
              <li>{t('legal_privacy_legal_basis_2')}</li>
              <li>{t('legal_privacy_legal_basis_3')}</li>
            </ul>
          ),
        },
        {
          id: 'privacy-retention',
          title: t('legal_privacy_section_5_title'),
          content: <p>{t('legal_privacy_retention_text')}</p>,
        },
        {
          id: 'privacy-rights',
          title: t('legal_privacy_section_6_title'),
          content: (
            <>
              <ul>
                <li>{t('legal_privacy_rights_1')}</li>
                <li>{t('legal_privacy_rights_2')}</li>
                <li>{t('legal_privacy_rights_3')}</li>
              </ul>
              <p>{t('legal_privacy_rights_contact_prefix')} <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a>.</p>
            </>
          ),
        },
        {
          id: 'privacy-transfers',
          title: t('legal_privacy_section_7_title'),
          content: <p>{t('legal_privacy_transfers_text')}</p>,
        },
        {
          id: 'cookies',
          title: t('legal_privacy_section_8_title'),
          content: (
            <>
              <p>{t('legal_cookies_intro')}</p>
              <ul>
                <li><strong>{t('legal_cookies_necessary_label')}</strong> {t('legal_cookies_necessary_text')}</li>
                <li><strong>{t('legal_cookies_analytics_label')}</strong> {t('legal_cookies_analytics_text')}</li>
                <li><strong>{t('legal_cookies_third_label')}</strong> {t('legal_cookies_third_text')}</li>
              </ul>
              <p>
                {t('legal_cookies_change')}{' '}
                <button type="button" className="legal-inline-button" onClick={openCookieSettings}>{t('cookies_settings')}</button>
              </p>
            </>
          ),
        },
      ],
    },
    {
      id: 'terms-section',
      title: t('legal_terms_title'),
      intro: t('legal_terms_intro'),
      sections: [
        { id: 'terms-object', title: t('legal_terms_section_1_title'), content: <p>{t('legal_terms_section_1_text')}</p> },
        {
          id: 'terms-roles',
          title: t('legal_terms_section_2_title'),
          content: (
            <ul>
              <li><strong>{t('legal_terms_leaders_label')}</strong> {t('legal_terms_leaders_text')}</li>
              <li><strong>{t('legal_terms_employees_label')}</strong> {t('legal_terms_employees_text')}</li>
            </ul>
          ),
        },
        { id: 'terms-3', title: t('legal_terms_section_3_title'), content: <p>{t('legal_terms_section_3_text')}</p> },
        { id: 'terms-4', title: t('legal_terms_section_4_title'), content: <p>{t('legal_terms_section_4_text')}</p> },
        { id: 'terms-5', title: t('legal_terms_section_5_title'), content: <p>{t('legal_terms_section_5_text')}</p> },
        { id: 'terms-6', title: t('legal_terms_section_6_title'), content: <p>{t('legal_terms_section_6_text')}</p> },
        { id: 'terms-7', title: t('legal_terms_section_7_title'), content: <p>{t('legal_terms_section_7_text')}</p> },
        { id: 'terms-8', title: t('legal_terms_section_8_title'), content: <p>{t('legal_terms_section_8_text')}</p> },
      ],
    },
  ], [t]);

  // El índice marca el apartado que se está leyendo
  useEffect(() => {
    const cards = document.querySelectorAll('.legal-card[id]');
    if (!cards.length || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -70% 0px' }
    );
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [groups]);

  return (
    <div className="brand-page-stack legal-page">
      <section className="section-band">
        <div className="section-band__inner">
          <header className="legal-hero">
            <SectionEyebrow>{t('legal_kicker')}</SectionEyebrow>
            <h1 className="legal-hero__title">{t('legal_title')}</h1>
            <p className="section-band__lead legal-hero__lead">{t('legal_intro')}</p>
            <div className="legal-hero__meta">
              <span className="legal-chip">
                <CalendarDays size={15} strokeWidth={2} aria-hidden="true" />
                {t('legal_last_updated_label')}: <strong>{LAST_UPDATED}</strong>
              </span>
              <a className="legal-chip legal-chip--link" href={`mailto:${LEGAL_EMAIL}`}>
                <Mail size={15} strokeWidth={2} aria-hidden="true" />
                {t('legal_channel_label')}: <strong>{LEGAL_EMAIL}</strong>
              </a>
            </div>
          </header>

          <div className="legal-layout">
            <nav className="legal-toc" aria-label={t('legal_index')}>
              <p className="legal-toc__title">{t('legal_index')}</p>
              {groups.map((group) => (
                <div key={group.id} className="legal-toc__group">
                  <a href={`#${group.id}`} className="legal-toc__group-title">{group.title}</a>
                  <ul className="legal-toc__list">
                    {group.sections.map((section) => (
                      <li key={section.id}>
                        <a
                          href={`#${section.id}`}
                          className={`legal-toc__link ${activeId === section.id ? 'is-active' : ''}`.trim()}
                          aria-current={activeId === section.id ? 'true' : undefined}
                        >
                          {section.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>

            <div className="legal-content">
              {groups.map((group) => (
                <section key={group.id} id={group.id} className="legal-block">
                  <h2 className="legal-block__title">{group.title}</h2>
                  <p className="legal-block__lead">{group.intro}</p>

                  <div className="legal-cards">
                    {group.sections.map((section, index) => (
                      <article key={section.id} id={section.id} className="legal-card">
                        <span className="legal-card__num" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                        <div className="legal-card__body">
                          <h3 className="legal-card__title">{section.title}</h3>
                          {section.content}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ))}

              <section id="contact-section" className="legal-cta">
                <SectionEyebrow>{t('legal_contact_kicker')}</SectionEyebrow>
                <h2 className="legal-cta__title">{t('legal_contact_title')}</h2>
                <p className="legal-cta__text">{t('legal_contact_text')}</p>
                <a href={`mailto:${LEGAL_EMAIL}`} className="btn btn--primary">
                  <Mail size={16} strokeWidth={2} aria-hidden="true" />
                  {LEGAL_EMAIL}
                </a>
              </section>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
